import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import styled from 'styled-components'
import BigNumber from 'bignumber.js'
import { Heading, Flex, Text, Button, Card, CardBody } from '@pancakeswap/uikit'
import { useTranslation } from 'contexts/Localization'
import useIntersectionObserver from 'hooks/useIntersectionObserver'
import { latinise } from 'utils/latinise'
import { orderBy } from 'lodash'
import FlexLayout from 'components/Layout/Flex'
import Page from 'components/Layout/Page'
import PageHeader from 'components/PageHeader'
import SearchInput from 'components/SearchInput'
import Select, { OptionProps } from 'components/Select/Select'
import { useUserPoolStakedOnly, useUserPoolsViewMode } from 'state/user/hooks'
import { ViewMode } from 'state/user/actions'
import Loading from 'components/Loading'
import ConnectWalletButton from 'components/ConnectWalletButton'
import { fetchPools, PoolItem, useXrpl } from '../../xrpl'
import PoolTabButtons from './components/PoolTabButtons'

const CardLayout = styled(FlexLayout)`
  justify-content: center;
`

const PoolControls = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  position: relative;

  justify-content: space-between;
  flex-direction: column;
  margin-bottom: 32px;

  ${({ theme }) => theme.mediaQueries.sm} {
    flex-direction: row;
    flex-wrap: wrap;
    padding: 16px 32px;
    margin-bottom: 0;
  }
`

const FilterContainer = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  padding: 8px 0px;

  ${({ theme }) => theme.mediaQueries.sm} {
    width: auto;
    padding: 0;
  }
`

const LabelWrapper = styled.div`
  > ${Text} {
    font-size: 12px;
  }
`

const ControlStretch = styled(Flex)`
  > div {
    flex: 1;
  }
`

const PoolCardShell = styled(Card)`
  align-self: baseline;
  margin: 0 8px 24px;

  ${({ theme }) => theme.mediaQueries.sm} {
    margin: 0 12px 46px;
  }
`

const PoolCardBody = styled(CardBody)`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const PoolRow = styled(Flex)`
  justify-content: space-between;
  align-items: center;
`

const PoolTableContainer = styled.div`
  filter: ${({ theme }) => theme.card.dropShadow};
  width: 100%;
  background: ${({ theme }) => theme.card.background};
  border-radius: 16px;
  margin: 16px 0px;
  overflow: hidden;
`

const PoolTableHeader = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr;
  padding: 16px 24px;
  border-bottom: 2px solid ${({ theme }) => theme.colors.cardBorder};
  font-size: 12px;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.textSubtle};
`

const PoolTableRow = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr;
  padding: 16px 24px;
  border-bottom: 2px solid ${({ theme }) => theme.colors.cardBorder};
  align-items: center;
`

const SideCard = styled(Card)`
  width: 100%;
  flex: 1;

  ${({ theme }) => theme.mediaQueries.sm} {
    min-width: 240px;
  }
`

const NUMBER_OF_POOLS_VISIBLE = 12

const Pools: React.FC = () => {
  const location = useLocation()
  const { t } = useTranslation()
  const { account } = useXrpl()
  const [stakedOnly, setStakedOnly] = useUserPoolStakedOnly()
  const [viewMode, setViewMode] = useUserPoolsViewMode()
  const [numberOfPoolsVisible, setNumberOfPoolsVisible] = useState(NUMBER_OF_POOLS_VISIBLE)
  const { observerRef, isIntersecting } = useIntersectionObserver()
  const [searchQuery, setSearchQuery] = useState('')
  const [sortOption, setSortOption] = useState('hot')
  const chosenPoolsLength = useRef(0)
  const [pools, setPools] = useState<PoolItem[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const [finishedPools, openPools] = useMemo(() => {
    const finished = pools.filter((pool) => pool.isFinished)
    const open = pools.filter((pool) => !pool.isFinished)
    return [finished, open]
  }, [pools])

  const stakedOnlyFinishedPools = useMemo(
    () => finishedPools.filter((pool) => new BigNumber(pool.stakedBalance || 0).gt(0)),
    [finishedPools],
  )
  const stakedOnlyOpenPools = useMemo(
    () => openPools.filter((pool) => new BigNumber(pool.stakedBalance || 0).gt(0)),
    [openPools],
  )
  const hasStakeInFinishedPools = stakedOnlyFinishedPools.length > 0

  useEffect(() => {
    let isMounted = true

    const loadPools = async () => {
      setLoading(true)
      setError('')
      try {
        const data = await fetchPools()
        if (isMounted) {
          setPools(data)
        }
      } catch (fetchError) {
        if (isMounted) {
          setError(fetchError.message || 'Failed to load pools')
        }
      } finally {
        if (isMounted) {
          setLoading(false)
        }
      }
    }

    loadPools()

    return () => {
      isMounted = false
    }
  }, [account])

  useEffect(() => {
    if (isIntersecting) {
      setNumberOfPoolsVisible((poolsCurrentlyVisible) => {
        if (poolsCurrentlyVisible <= chosenPoolsLength.current) {
          return poolsCurrentlyVisible + NUMBER_OF_POOLS_VISIBLE
        }
        return poolsCurrentlyVisible
      })
    }
  }, [isIntersecting])

  const showFinishedPools = location.pathname.includes('history')

  const handleChangeSearchQuery = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value)
  }

  const handleSortOptionChange = (option: OptionProps): void => {
    setSortOption(option.value)
  }

  const sortPools = (poolsToSort: PoolItem[]) => {
    switch (sortOption) {
      case 'apr':
        return orderBy(poolsToSort, (pool: PoolItem) => pool.apr ?? 0, 'desc')
      case 'earned':
        return orderBy(poolsToSort, (pool: PoolItem) => Number(pool.earned || 0), 'desc')
      case 'totalStaked':
        return orderBy(poolsToSort, (pool: PoolItem) => Number(pool.stakedBalance || 0), 'desc')
      default:
        return poolsToSort
    }
  }

  let chosenPools: PoolItem[]
  if (showFinishedPools) {
    chosenPools = stakedOnly ? stakedOnlyFinishedPools : finishedPools
  } else {
    chosenPools = stakedOnly ? stakedOnlyOpenPools : openPools
  }

  if (searchQuery) {
    const lowercaseQuery = latinise(searchQuery.toLowerCase())
    chosenPools = chosenPools.filter((pool) => latinise(pool.name.toLowerCase()).includes(lowercaseQuery))
  }

  chosenPools = sortPools(chosenPools).slice(0, numberOfPoolsVisible)
  chosenPoolsLength.current = chosenPools.length

  const renderCard = (pool: PoolItem) => {
    return (
      <PoolCardShell key={pool.id}>
        <PoolCardBody>
          <Text bold fontSize="16px">
            {pool.name}
          </Text>
          <PoolRow>
            <Text color="textSubtle">{t('APR')}</Text>
            <Text>{pool.apr ?? '-'}</Text>
          </PoolRow>
          <PoolRow>
            <Text color="textSubtle">{t('Staked')}</Text>
            <Text>{pool.stakedBalance ?? '0'}</Text>
          </PoolRow>
          <PoolRow>
            <Text color="textSubtle">{t('Earned')}</Text>
            <Text>
              {pool.earned ?? '0'} {pool.rewardSymbol || ''}
            </Text>
          </PoolRow>
          {account ? (
            <Button width="100%">{t('Manage')}</Button>
          ) : (
            <ConnectWalletButton width="100%" />
          )}
        </PoolCardBody>
      </PoolCardShell>
    )
  }

  const renderTable = () => {
    if (!chosenPools.length) {
      return null
    }

    return (
      <PoolTableContainer>
        <PoolTableHeader>
          <div>{t('Pool')}</div>
          <div>{t('APR')}</div>
          <div>{t('Staked')}</div>
          <div>{t('Earned')}</div>
        </PoolTableHeader>
        {chosenPools.map((pool) => (
          <PoolTableRow key={pool.id}>
            <Text bold>{pool.name}</Text>
            <Text>{pool.apr ?? '-'}</Text>
            <Text>{pool.stakedBalance ?? '0'}</Text>
            <Flex alignItems="center" justifyContent="space-between">
              <Text>
                {pool.earned ?? '0'} {pool.rewardSymbol || ''}
              </Text>
              {account ? <Button scale="sm">{t('Manage')}</Button> : <ConnectWalletButton scale="sm" />}
            </Flex>
          </PoolTableRow>
        ))}
      </PoolTableContainer>
    )
  }

  const cardLayout = <CardLayout>{chosenPools.map((pool) => renderCard(pool))}</CardLayout>

  return (
    <>
      <PageHeader>
        <Flex justifyContent="space-between" flexDirection={['column', null, null, 'row']}>
          <Flex flex="1" flexDirection="column" mr={['8px', 0]}>
            <Heading as="h1" scale="xxl" color="secondary" mb="24px">
              {t('BESC Pools')}
            </Heading>
            <Heading scale="md" color="text">
              {t('Just stake some tokens to earn.')}
            </Heading>
            <Heading scale="md" color="text">
              {t('High APR, low risk.')}
            </Heading>
          </Flex>
          <Flex flex="1" height="fit-content" justifyContent="center" alignItems="center" mt={['24px', null, '0']}>
            <SideCard>
              <CardBody>
                <Text bold mb="8px">
                  {t('XRPL Pools')}
                </Text>
                <Text fontSize="14px" color="textSubtle">
                  {t('Pool rewards are tracked off-chain and paid via XRPL payments.')}
                </Text>
              </CardBody>
            </SideCard>
          </Flex>
        </Flex>
      </PageHeader>

      <Page>
        <PoolControls>
          <PoolTabButtons
            stakedOnly={stakedOnly}
            setStakedOnly={setStakedOnly}
            hasStakeInFinishedPools={hasStakeInFinishedPools}
            viewMode={viewMode}
            setViewMode={setViewMode}
          />
          <FilterContainer>
            <LabelWrapper>
              <Text fontSize="12px" bold color="textSubtle" textTransform="uppercase">
                {t('Sort by')}
              </Text>
              <ControlStretch>
                <Select
                  options={[
                    { label: t('Hot'), value: 'hot' },
                    { label: t('APR'), value: 'apr' },
                    { label: t('Earned'), value: 'earned' },
                    { label: t('Total staked'), value: 'totalStaked' },
                  ]}
                  onOptionChange={handleSortOptionChange}
                />
              </ControlStretch>
            </LabelWrapper>
            <LabelWrapper style={{ marginLeft: 16 }}>
              <Text fontSize="12px" bold color="textSubtle" textTransform="uppercase">
                {t('Search')}
              </Text>
              <SearchInput onChange={handleChangeSearchQuery} placeholder="Search Pools" />
            </LabelWrapper>
          </FilterContainer>
        </PoolControls>
        {showFinishedPools && (
          <Text fontSize="20px" color="failure" pb="32px">
            {t('These pools are no longer distributing rewards. Please unstake your tokens.')}
          </Text>
        )}
        {error && (
          <Text color="failure" mb="16px">
            {error}
          </Text>
        )}
        {loading ? (
          <Flex justifyContent="center" mb="4px">
            <Loading />
          </Flex>
        ) : !chosenPools.length ? (
          <Text color="textSubtle">{t('No pools available.')}</Text>
        ) : viewMode === ViewMode.CARD ? (
          cardLayout
        ) : (
          renderTable()
        )}
        <div ref={observerRef} />
      </Page>
    </>
  )
}

export default Pools
